/**
 * Modal: publish a proven network release to the shared-services **network
 * registry** (doc/arch/vertical/NETWORK-REGISTRY.md).
 *
 * The backend does the heavy lifting: it builds a portable package (manifest +
 * catalog + context + skills) from the release, suggests a catalog doc with Haiku,
 * runs the portability guard, and writes a DRAFT row + zip cross-account. This
 * dialog is a thin shell: the creator can OVERRIDE the catalog fields
 * (title/summary/domain/tags) — anything left blank, Haiku fills in. On success
 * it shows the resulting DRAFT package (version, domain, skill count) so the
 * creator knows it landed and still needs a Release step to go live.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { publishNetworkToRegistry, getRegistryCatalogPreview } from '@/services/networkRestoreApi';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import { WORKBENCH_OUTLINE_BTN_MODAL_LG } from '@/utils/workbenchOutlineButtonClassNames.js';

// Mirrors CATALOG_DOMAINS in stepflow_core/network_package.py — keep in sync.
// The backend snaps any unknown value to "General", so a drift here is safe
// (just a stale option), never a hard failure.
export const CATALOG_DOMAINS = [
  'General', 'Engineering/Tech', 'Data & Analytics', 'Content & Writing',
  'Customer & Support', 'Sales & Marketing', 'Finance & Ops',
  'Legal & Compliance', 'Research', 'Healthcare/Life-Sci', 'Education',
];

function parseTags(raw) {
  return String(raw || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 8);
}

export default function PublishToRegistryDialog({
  open,
  onClose,
  networkId,
  networkName,
  releaseEntry,
  onPublished,
}) {
  const releaseVersion = Number(releaseEntry?.releaseVersion);
  // The network's SEMANTIC label (e.g. "1.00") — the authoritative stepflowVersion
  // the rest of the app shows, NOT the raw integer. Keeps publish/browse/upgrade
  // version-consistent. Falls back to the int only when no semantic label exists.
  const versionLabel = displayVersionLabel(releaseEntry)
    || (Number.isFinite(releaseVersion) ? `v${releaseVersion}` : '');

  // Catalog overrides — all optional. Blank fields let Haiku fill them in.
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [domain, setDomain] = useState(''); // '' → let Haiku suggest
  const [tagsRaw, setTagsRaw] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // PublishToRegistryResponse on success

  // Start true so the very first render after opening shows the spinner (not a
  // one-frame flash of the empty form before the fetch effect runs).
  const [prefilling, setPrefilling] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    // Reset each time the dialog opens for a (possibly different) release.
    setTitle(networkName || '');
    setSummary('');
    setDomain('');
    setTagsRaw('');
    setSubmitting(false);
    setError(null);
    setResult(null);

    // Prefill ALL four fields from the architect-authored catalog metadata
    // (title/summary/domain/tags) so the creator edits rather than fills blanks.
    // Best-effort: a failure just leaves the Title-only prefill (old behavior).
    let cancelled = false;
    setPrefilling(true);
    getRegistryCatalogPreview(networkId, Number.isFinite(releaseVersion) ? releaseVersion : undefined)
      .then((d) => {
        if (cancelled || !d) return;
        if (d.title) setTitle(d.title);
        if (d.summary) setSummary(d.summary);
        if (d.domain) setDomain(d.domain);
        if (Array.isArray(d.tags) && d.tags.length) setTagsRaw(d.tags.join(', '));
      })
      .catch(() => { /* keep Title-only prefill */ })
      .finally(() => { if (!cancelled) setPrefilling(false); });
    return () => { cancelled = true; };
  }, [open, networkName, releaseVersion, networkId]);

  const canSubmit = useMemo(
    () => !submitting && Number.isFinite(releaseVersion) && releaseVersion >= 1,
    [submitting, releaseVersion],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const body = { version: releaseVersion };
      if (title.trim()) body.title = title.trim();
      if (summary.trim()) body.summary = summary.trim();
      if (domain) body.domain = domain;
      const tags = parseTags(tagsRaw);
      if (tags.length) body.tags = tags;
      const res = await publishNetworkToRegistry(networkId, body);
      setResult(res);
      onPublished?.(res);
    } catch (err) {
      // The API throws the raw response body (a JSON string) on error. Surface
      // the friendly ``message``/``error`` field rather than raw JSON.
      let msg = err?.message || 'Publish failed';
      try {
        const parsed = JSON.parse(msg);
        msg = parsed?.message || parsed?.error || msg;
      } catch { /* not JSON — show as-is */ }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && onClose()}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-dialog-title"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="relative z-[101] max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
          >
            <h2 id="publish-dialog-title" className="text-lg font-semibold text-gray-900">
              Release to registry
            </h2>

            {result ? (
              // ---- success view ------------------------------------------
              <div className="mt-3">
                <p className="text-sm text-gray-600">
                  Published <span className="font-medium text-gray-800">{networkName}</span>
                  {versionLabel ? ` (${versionLabel})` : ''} to the registry as a
                  {' '}<span className="font-medium">Draft</span>.
                </p>
                <dl className="mt-4 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Version</dt>
                    <dd className="font-medium text-gray-900">{result.stepflowVersion || `v${result.version}`}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Title</dt>
                    <dd className="font-medium text-gray-900">{result.catalog?.title}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Domain</dt>
                    <dd className="font-medium text-gray-900">{result.catalog?.domain}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Agents / Skills</dt>
                    <dd className="font-medium text-gray-900">
                      {result.catalog?.agentCount ?? 0} / {result.skillCount ?? 0}
                    </dd>
                  </div>
                  {Array.isArray(result.catalog?.tags) && result.catalog.tags.length ? (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {result.catalog.tags.map((t) => (
                        <span key={t} className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </dl>
                <p className="mt-3 text-xs text-gray-500">
                  It stays a <span className="font-medium">Draft</span> until released for
                  browsing. Publishing again cuts a new package version.
                </p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className={WORKBENCH_OUTLINE_BTN_MODAL_LG}
                    onClick={() => onClose()}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : prefilling ? (
              // ---- loading the architect-authored catalog defaults -------
              // Wait for the preview to return, THEN render the form fully
              // populated at once — instead of a blank form whose fields visibly
              // fill in a beat later (reads as a glitch).
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500"
                   data-testid="publish-dialog-prefill-spinner">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden />
                Preparing catalog details…
              </div>
            ) : (
              // ---- form view ---------------------------------------------
              <form onSubmit={handleSubmit} className="mt-3">
                <p className="text-sm text-gray-600">
                  Publishes <span className="font-medium text-gray-800">{networkName}</span>
                  {versionLabel ? ` (${versionLabel})` : ''} as a portable package
                  (agents, skills, rules) to the shared registry. Leave fields blank to let
                  the catalog details be suggested automatically.
                </p>

                <fieldset className="mt-4 space-y-4" disabled={submitting}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700" htmlFor="pub-title">
                      Title <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="pub-title"
                      type="text"
                      value={title}
                      onChange={(ev) => setTitle(ev.target.value)}
                      maxLength={120}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="A product-style name"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700" htmlFor="pub-summary">
                      Summary <span className="font-normal text-gray-400">(optional — override)</span>
                    </label>
                    <textarea
                      id="pub-summary"
                      value={summary}
                      onChange={(ev) => setSummary(ev.target.value)}
                      maxLength={2000}
                      rows={4}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Leave blank to use the network's own architecture description."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Defaults to the architect's description of this network. Only fill this in
                      to override it.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700" htmlFor="pub-domain">
                      Domain <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <select
                      id="pub-domain"
                      value={domain}
                      onChange={(ev) => setDomain(ev.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Auto-suggest</option>
                      {CATALOG_DOMAINS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700" htmlFor="pub-tags">
                      Tags <span className="font-normal text-gray-400">(optional, comma-separated)</span>
                    </label>
                    <input
                      id="pub-tags"
                      type="text"
                      value={tagsRaw}
                      onChange={(ev) => setTagsRaw(ev.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="e.g. novel, pdf, code-review"
                      autoComplete="off"
                    />
                  </div>
                </fieldset>

                {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    onClick={() => !submitting && onClose()}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={WORKBENCH_OUTLINE_BTN_MODAL_LG}
                    disabled={!canSubmit}
                  >
                    {submitting ? 'Publishing…' : 'Publish'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
